import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { ArrowRight, CheckCircle, Clock, Box, Package, ShieldCheck, RefreshCw, Sparkles, Users, Stethoscope } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import React from 'react';
import { principles } from '../data/about';
import { OrganicShapes } from '../components/decor/OrganicShapes';
import { Reveal } from '../components/Reveal';

const logoWhite = new URL('../assets/logo-ph-white.svg', import.meta.url).href;
const nirmalPhoto = new URL('../assets/nirmal-headshot.jpg', import.meta.url).href;

const team = [
  {
    name: 'Nirmal Ranganathan',
    title: 'Founder',
    photo: nirmalPhoto,
    bio: [
      'Nirmal is a technology executive with twenty one years leading engineering, data and AI strategy, most recently as CTO of Public Cloud at Rackspace Technology, where he shaped the roadmap for its AI-powered solutions portfolio and worked with customers, including major healthcare organizations, to take ideas from strategy through deployment.',
      'That leadership sits on deep engineering roots. He built products and services with AI embedded at their core and grew a Data & AI practice from an idea into a real business. Along the way he sat alongside healthcare customers working through some of their hardest technology problems, close enough to see firsthand where the gaps in the system actually live.',
      'He founded PleromaHealth to solve exactly those problems and close the gaps patients fall through.',
    ],
  },
];

const whyUs = [
  {
    icon: Package,
    title: 'Software and services, combined.',
    description: 'Every engagement leaves you with software that is both customizable and ships ready to use out of the box. We don\'t hand it over and walk away, we manage the full lifecycle of how you use it.',
  },
  {
    icon: ShieldCheck,
    title: 'The platform and the IP are entirely yours.',
    description: 'Runs on native systems you own outright, with no proprietary format and no lock-in. Customer-hosted, air-gapped, and Pleroma-managed within your walls. HIPAA and SOC compliant throughout.',
  },
  {
    icon: RefreshCw,
    title: 'Complementary, not a rip-and-replace.',
    description: 'Runs under or beside the population health platform you already use. Once configured, it adapts as you use it, so it sharpens over time instead of going stale.',
  },
  {
    icon: Sparkles,
    title: 'We eliminate the long tail of point solutions.',
    description: 'We advise on how to actually apply AI in healthcare and deliver integrated solutions, because we\'ve been the people stitching point tools together and know which seams to close first.',
  },
];

export function AboutPage() {
  const location = useLocation();

  React.useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      window.requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }
  }, [location]);

  return (
    <div className="min-h-screen">
      <Header />

      {/* 1. Who we are / why we exist */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#f9fbfc] via-white to-[#F4F7F9] py-16 lg:py-24">
        <OrganicShapes variant="hero" />
        <Reveal className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl lg:text-6xl text-[#1f2937] mb-6 leading-tight">
            We built the systems that patients fall through.
          </h1>
          <p className="text-xl text-[#4B5563] leading-relaxed">
            We spent our careers inside them. Decades running utilization management, care management, claims, and eligibility at some of the largest payers and health-services organizations in the country. We ran the review queues where prior authorizations waited. We built the care-management worklists that carried a member's name and never got worked. We know how to fix these problems because we were on the ground when they happened.
          </p>
        </Reveal>
      </section>

      {/* 2. Built from the inside */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 items-center gap-12">
            <Reveal className="lg:pr-16" direction="right">
              <h2 className="text-4xl text-[#1f2937] mb-6">Built from the inside.</h2>
              <div className="space-y-4 text-lg text-[#4B5563] leading-relaxed">
                <p>
                  We built and operated the systems where that context disappears, so we know exactly where it happens: the referral that gets faxed and re-keyed by hand, the prior auth where a diagnosis code from the hospital's EHR never makes it into the payer's system, the care-management worklist that inherits a member's name and ID but not the chart behind it.
                </p>
                <p>
                  Our member model is built on that same ground, years spent running eligibility, claims, utilization management, and care management, watching one member's record get reconstructed differently by every system that touched it. We know which version to trust because we're the ones who watched it drift.
                </p>
              </div>
            </Reveal>
            <Reveal as="div" direction="left" delay={120} className="rounded-2xl p-8 text-white" style={{ background: 'linear-gradient(to bottom right, #0f172a, #1e3a5f)' }}>
              <p className="text-[#34AFFB] font-semibold mb-3 text-sm uppercase tracking-wider">The name</p>
              <p className="text-2xl font-semibold mb-3">Pleroma</p>
              <p className="text-white/80 leading-relaxed">
                From the Greek word for fullness, completeness. Healthcare has been broken into fragments by systems, by vendors, by incentives and the cost lands on patients and the people trying to care for them. We exist to put the pieces back into a whole.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 3. How we deliver — Services-as-Software */}
      <section id="how-we-deliver" className="py-20 bg-[#F4F7F9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-14">
            <span className="border-l-4 border-[#34AFFB] pl-3 text-[#34AFFB] text-sm font-semibold inline-block mb-6">
              How We Deliver
            </span>
            <h2 className="text-4xl text-[#1f2937] mb-4">Services-as-Software.</h2>
            <p className="text-lg text-[#4B5563] max-w-2xl mx-auto">
              Most healthcare work is bought as consulting or SaaS. Both leave value on the table. We do a third thing: ship working software you own, and stay for the lifecycle rather than handing over a tool and walking away.
            </p>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6 items-stretch">
            <Reveal as="div" className="bg-white rounded-2xl p-8 flex flex-col">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 bg-[#4B5563]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock className="h-5 w-5 text-[#4B5563]/50" />
                </div>
                <h3 className="text-lg font-semibold text-[#4B5563]">Traditional consulting</h3>
              </div>
              <p className="text-base text-[#4B5563]/70 leading-relaxed mb-6">
                Billable hours create dependencies and the knowledge leaves with the team, with no lasting software asset remaining when the engagement ends.
              </p>
              <ul className="mt-auto" style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
                {['Hours billed, not outcomes', 'Knowledge leaves with the team', 'No asset at engagement end'].map((pt, i) => (
                  <li key={i} className="text-base text-[#4B5563]/60 flex items-center gap-2.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#4B5563]/30 flex-shrink-0" />
                    {pt}
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal as="div" delay={120} className="bg-white rounded-2xl p-8 flex flex-col">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 bg-[#4B5563]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Box className="h-5 w-5 text-[#4B5563]/50" />
                </div>
                <h3 className="text-lg font-semibold text-[#4B5563]">SaaS vendor</h3>
              </div>
              <p className="text-base text-[#4B5563]/70 leading-relaxed mb-6">
                Delivers the software, then leaves you to figure it out. Your organization bends to the product, rather than the product being shaped around your organization.
              </p>
              <ul className="mt-auto" style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
                {['Generic, not purpose-built', 'You adapt to their product', 'Perpetual license, no ownership'].map((pt, i) => (
                  <li key={i} className="text-base text-[#4B5563]/60 flex items-center gap-2.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#4B5563]/30 flex-shrink-0" />
                    {pt}
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal as="div" delay={240} className="rounded-2xl p-8 flex flex-col text-white" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)' }}>
              <div className="flex items-center gap-3 mb-4">
                <img src={logoWhite} alt="PleromaHealth" className="h-10" />
              </div>
              <p className="text-base text-white/70 leading-relaxed mb-6">
                Ships working software you own. Built for your problem, with the IP compounding across engagements, and we stay for the lifecycle.
              </p>
              <ul className="mt-auto" style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
                {['Software you own outright', 'Built for your exact problem', 'IP compounds across engagements'].map((pt, i) => (
                  <li key={i} className="text-base text-white/80 flex items-center gap-4">
                    <CheckCircle className="h-3.5 w-3.5 text-[#70D6C5] flex-shrink-0" />
                    {pt}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          <p className="text-center text-[#4B5563] mt-16 max-w-2xl mx-auto">
            We don't build every layer ourselves, we partner the commodity plumbing (cloud and data platforms, delivery organizations that extend our reach) and concentrate engineering on what's differentiated.
          </p>
        </div>
      </section>

      {/* 4. Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border border-[#e2e8ec] rounded-2xl overflow-hidden bg-[#e2e8ec]">
            <div className="grid md:grid-cols-3 gap-px">
              {/* Heading cell */}
              <Reveal as="div" className="bg-[#f4f7f9] p-8 lg:p-12 flex flex-col justify-center">
                <span className="border-l-4 border-[#34AFFB] pl-3 text-[#34AFFB] text-sm font-semibold inline-block mb-6">
                  What We Believe
                </span>
                <h2 className="text-4xl lg:text-5xl text-[#1f2937] leading-tight">
                  Convictions we build on.
                </h2>
              </Reveal>

              {principles.map((principle, index) => {
                const Icon = principle.icon;
                return (
                  <Reveal as="div" key={index} delay={index * 90} className="bg-[#f9fbfc] p-8 lg:p-12 flex flex-col">
                    <div className="h-12 w-12 bg-[#34AFFB]/10 rounded-lg flex items-center justify-center mb-8">
                      <Icon className="h-6 w-6 text-[#34AFFB]" />
                    </div>
                    <p className="text-[#4B5563] leading-relaxed mt-auto">
                      <span className="font-semibold text-[#1f2937]">{principle.title}</span>{' '}
                      {principle.description}
                    </p>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* 5. Team */}
      <section className="py-20 bg-[#F4F7F9]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-3xl mx-auto text-center mb-14">
            <div className="h-14 w-14 bg-[#34AFFB]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Users className="h-7 w-7 text-[#34AFFB]" />
            </div>
            <h2 className="text-3xl text-[#1f2937] mb-4">Team</h2>
            <p className="text-lg text-[#4B5563]">
              We arrive at this problem from three angles — healthcare operations, data and AI, and healthcare data modeling.
            </p>
          </Reveal>

          <div className="grid gap-8">
            {team.map((member) => (
              <Reveal as="div" key={member.name} className="bg-white rounded-2xl p-8 lg:p-10 shadow-sm">
                <div className="flex flex-col sm:flex-row gap-6 sm:gap-8">
                  <img
                    src={member.photo}
                    alt={member.name}
                    className="h-28 w-28 rounded-full object-cover flex-shrink-0 mx-auto sm:mx-0"
                  />
                  <div>
                    <h3 className="text-xl font-semibold text-[#1f2937]">{member.name}</h3>
                    <p className="text-[#34AFFB] font-medium mb-4">{member.title}</p>
                    <div className="space-y-3 text-[#4B5563] leading-relaxed">
                      {member.bio.map((paragraph, i) => (
                        <p key={i}>{paragraph}</p>
                      ))}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}

            <Reveal as="div" className="bg-white rounded-2xl p-8 lg:p-10 shadow-sm text-center">
              <div className="h-12 w-12 bg-[#34AFFB]/10 rounded-xl flex items-center justify-center mx-auto mb-6">
                <Stethoscope className="h-6 w-6 text-[#34AFFB]" />
              </div>
              <h3 className="text-lg font-semibold text-[#1f2937] mb-3">Deep domain expertise</h3>
              <p className="text-[#4B5563] leading-relaxed max-w-2xl mx-auto">
                The team pairs that technology and AI leadership with health informatics and healthcare operations experience: decades running utilization management, care management, claims, and eligibility inside some of the largest payers and health-services organizations in the country. That domain knowledge, combined with modern data and AI architecture, is what lets PleromaHealth move from diagnosing where the system breaks down to fixing it.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 6. Why us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-16">
            <h2 className="text-4xl text-[#1f2937] mb-4">Why us</h2>
            <p className="text-xl text-[#4B5563] max-w-2xl mx-auto">
              Our business model is different, we make it simple for the customer to own and manage the software.
            </p>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-8">
            {whyUs.map((item, index) => {
              const Icon = item.icon;
              return (
                <Reveal as="div" key={index} delay={(index % 2) * 120} className="bg-[#f9fbfc] rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
                  <div className="h-12 w-12 bg-[#34AFFB]/10 rounded-xl flex items-center justify-center mb-6">
                    <Icon className="h-6 w-6 text-[#34AFFB]" />
                  </div>
                  <h3 className="text-lg font-medium text-[#1f2937] mb-3">{item.title}</h3>
                  <p className="text-[#4B5563]">{item.description}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 7. CTA */}
      <section className="relative overflow-hidden py-20 bg-gradient-to-r from-[#34AFFB] to-[#70D6C5]">
        <OrganicShapes variant="band" />
        <Reveal className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl text-white mb-4">Let's build something that works.</h2>
          <p className="text-xl text-white/90 mb-8">
            Tell us about the problem. We'll tell you, plainly, whether and how we can help.
          </p>
          <Button asChild size="lg" className="bg-white text-[#34AFFB] hover:bg-white/90">
            <Link to="/#contact">
              Book a demo
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </Reveal>
      </section>

      <Footer />
    </div>
  );
}
